import { formatToForm } from '@mixins/currency';
import Model from '@models/model';

export const ITEM_TYPE_CHARGE = 1;
export const ITEM_TYPE_LABOR = 2;
export const ITEM_TYPE_PART = 3;
export const ITEM_TYPE_SERVICE = 4;
export const ITEM_TYPE_SUBSCRIPTION = 5;
export const ITEM_TYPE_TANK = 6;
export const ITEM_TYPE_TRAILER = 7;
export const ITEM_TYPE_MEMBRANE_CLEANING = 8;
export const ITEM_TYPE_ALL = 9;
export const ITEM_TYPES = [
    { id: ITEM_TYPE_TANK, text: 'Tank' },
    { id: ITEM_TYPE_SERVICE, text: 'Service' },
    { id: ITEM_TYPE_PART, text: 'Part' },
    { id: ITEM_TYPE_CHARGE, text: 'Charge' },
    { id: ITEM_TYPE_SUBSCRIPTION, text: 'Subscription' },
    { id: ITEM_TYPE_LABOR, text: 'Labor' },
    { id: ITEM_TYPE_TRAILER, text: 'Trailer' },
    { id: ITEM_TYPE_MEMBRANE_CLEANING, text: 'Membrane Cleaning' },
    { id: ITEM_TYPE_ALL, text: 'All' },
];

const FREIGHT_CHARGE_EXCHANGE_ITEM_ID = 7003;
const FREIGHT_CHARGE_SERVICE_ITEM_ID = 7004;
export const FUEL_SURCHARGE_EXCHANGE_ITEM_ID = 6993;
export const FUEL_SURCHARGE_SERVICE_ITEM_ID = 6994;
export const LABOR_SURCHARGE_EXCHANGE_ITEM_ID = 6725;
export const LABOR_SURCHARGE_SERVICE_ITEM_ID = 6725;
export const PREVAILING_WAGE_SURCHARGE_EXCHANGE_ITEM_ID = 6998;
export const PREVAILING_WAGE_SURCHARGE_SERVICE_ITEM_ID = 7000;
export const TRIP_SURCHARGE_EXCHANGE_ITEM_ID = 7001;
export const TRIP_CHARGE_SERVICE_ITEM_ID = 7002;
export const MOBILE_TRAILER_TRIP_SURCHARGE_ITEM_ID = 13502;
export const CUSTOM_ITEM_PART = 6992;
export const SALT_PURETEC_ITEM_NUMBER = '08-001';
export const LABOR_PURETEC_ITEM_NUMBER = '21-153';
export const LABOR_OT_PURETEC_ITEM_NUMBER = '21-166';
export const LABOR_DT_PURETEC_ITEM_NUMBER = '21-167';
export const TRAVEL_PURETEC_ITEM_NUMBER = '21-170';
export const TRAVEL_OT_PURETEC_ITEM_NUMBER = '21-171';
export const TRAVEL_DT_PURETEC_ITEM_NUMBER = '21-172';

export default class Item extends Model {
    resource() {
        return 'items';
    }

    relations() {
        return {
            exchange_item: Item,
            rent_item: Item,
            commodity_surcharge_item: Item,
            environmental_fee_item: Item,
        };
    }

    get formFieldCasts() {
        return {
            exchange_price: formatToForm,
            rent_price: formatToForm,
            commodity_surcharge_price: formatToForm,
            environmental_fee_price: formatToForm,
            cost: formatToForm,
            price: formatToForm,
        };
    }

    static getPrevailingWageItemForDelivery() {
        return this.with('glCode').find(PREVAILING_WAGE_SURCHARGE_EXCHANGE_ITEM_ID);
    }

    static getPrevailingWageItemForService() {
        return this.with('glCode').find(PREVAILING_WAGE_SURCHARGE_SERVICE_ITEM_ID);
    }

    static getPrevailingWageItemFromOrder(order) {
        if (order.isDeliveryOrder()) {
            return this.getPrevailingWageItemForDelivery();
        }
        if (order.isServiceOrder()) {
            return this.getPrevailingWageItemForService();
        }
        throw new Error('Order type not supported');
    }

    static getLaborSurchargeItemFromOrder(order) {
        if (order.isDeliveryOrder()) {
            return this.with('glCode').find(LABOR_SURCHARGE_EXCHANGE_ITEM_ID);
        } if (order.isServiceOrder()) {
            return this.with('glCode').find(LABOR_SURCHARGE_SERVICE_ITEM_ID);
        }
        throw new Error('Order type not supported');
    }

    static getFuelSurchargeItemFromOrder(order) {
        if (order.isDeliveryOrder()) {
            return this.with('glCode').find(FUEL_SURCHARGE_EXCHANGE_ITEM_ID);
        } if (order.isServiceOrder()) {
            return this.with('glCode').find(FUEL_SURCHARGE_SERVICE_ITEM_ID);
        }
        throw new Error('Order type not supported');
    }

    static getTripSurchargeItemFromOrder(order) {
        if (order.isDeliveryOrder()) {
            return this.with('glCode').find(TRIP_SURCHARGE_EXCHANGE_ITEM_ID);
        } if (order.isServiceOrder()) {
            return this.with('glCode').find(TRIP_CHARGE_SERVICE_ITEM_ID);
        }
        throw new Error('Order type not supported');
    }

    static getFreightChargeItemFromOrder(order) {
        if (order.isDeliveryOrder()) {
            return this.with('glCode').find(FREIGHT_CHARGE_EXCHANGE_ITEM_ID);
        } if (order.isServiceOrder()) {
            return this.with('glCode').find(FREIGHT_CHARGE_SERVICE_ITEM_ID);
        }
        throw new Error('Order type not supported');
    }

    static getMobileTrailerTripSurchargeItemFromOrder() {
        return this.with('glCode').find(MOBILE_TRAILER_TRIP_SURCHARGE_ITEM_ID);
    }

    static getItemList() {
        return this.with('defaultVendor', 'replacements');
    }

    getPriceByBranch(branchId) {
        if (this.branch_items && this.branch_items[branchId] && this.branch_items[branchId].is_active) {
            return this.branch_items[branchId].price || this.price || this.vendor?.vendor_price;
        }

        return this.price || this.getBaseCost();
    }

    static getPriceByBranch(item, branchId) {
        const self = new Item(item);

        return self.getPriceByBranch(branchId);
    }

    getBaseCost() {
        return this.landed_cost || this.vendor?.vendor_price || 0;
    }

    getDivisor(branchId) {
        if (this.branch_items && this.branch_items[branchId] && this.branch_items[branchId].is_active) {
            return this.branch_items[branchId].divisor;
        }

        return this.divisor || 0.58;
    }
}
